import { cn } from "@/lib/utils";

export interface ChatImageProps {
  /** Image URL. Component renders nothing when empty. */
  src: string;
  /** Accessible alt text for the image. */
  alt: string;
  /** Optional caption displayed below the image. */
  caption?: string | null;
  /** Round the image corners (default true). */
  rounded?: boolean | null;
  /** Maximum height in pixels (default 200 for chat density). */
  maxHeight?: number | null;
  /** Optional class name for the root figure element. */
  className?: string;
}

/**
 * Chat image: responsive, object-cover, optional caption. Uses plain `<img>`
 * (naapp does not use next/image). Compact default height (200px) for chat density.
 */
const ChatImage = ({
  src,
  alt,
  caption,
  rounded = true,
  maxHeight = 200,
  className,
}: ChatImageProps) => {
  const effectiveRounded = rounded !== false;
  const effectiveMaxHeight = maxHeight ?? 200;

  if (src === "") {
    return null;
  }

  return (
    <figure className={cn("m-0 w-full", className)}>
      <div
        className={cn(
          "relative w-full overflow-hidden",
          effectiveRounded && "rounded-lg",
        )}
        style={{ maxHeight: effectiveMaxHeight }}
      >
        <img
          src={src}
          alt={alt}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      {caption != null && caption !== "" ? (
        <figcaption className="mt-1 text-xs text-muted-foreground">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
};

export default ChatImage;
